// @flow

import * as React from 'react'
import moment from 'moment'
import sanitizeHtml from 'sanitize-html'
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Divider,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemText,
  Link,
  Grid,
  Radio,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { getContentItem } from 'services/cp-hcms'
import useIsMounted from 'hooks/useIsMounted'
import Pre from 'components/Pre'
import Fieldset from 'components/Fieldset'
import ErrorSnackbar from 'components/snackbars/ErrorSnackbar'

import ContentItemAsset from './ContentItemAsset'

const useStyles = makeStyles((theme) => ({
  textarea: {
    whiteSpace: 'pre-line',
  },
  images: {
    maxWidth: '100%',
    maxHeight: '300px',
    margin: 'auto',
    display: 'block',
  },
}))

type Props = {
  isOpen: boolean,
  integration: IntegrationCPHCMS,
  contentType: HCMSContentType,
  contentItem: HCMSContentItem,
  onClose: () => void,
}

const ContentItemDialog = React.memo<Props>(function ({
  isOpen,
  integration,
  contentType,
  contentItem,
  onClose,
}) {
  const classes = useStyles()

  const isMounted = useIsMounted()

  const [nestedState, setNestedState] = React.useState({
    error: null,
    contentItem: null,
    contentType: null,
    isFetching: false,
  })

  const removeNestedState = React.useCallback(() => {
    setNestedState({
      error: null,
      contentItem: null,
      contentType: null,
      isFetching: false,
    })
  }, [])

  const handleClickReference = React.useCallback(
    async ({ contentTypeField, referenceId }) => {
      setNestedState({
        error: null,
        contentItem: null,
        contentType: null,
        isFetching: true,
      })
      if (contentTypeField.properties.schemaId) {
        let newNestedState = null
        let newNestedError = null
        try {
          newNestedState = await getContentItem({
            integration,
            contentItemId: referenceId,
            contentTypeId: contentTypeField.properties.schemaId,
          })
        } catch (error) {
          newNestedError = error
        }
        if (isMounted.current) {
          setNestedState({
            contentItem: null,
            contentType: null,
            isFetching: false,
            error: newNestedError,
            ...newNestedState,
          })
        }
      }
    },
    [integration, isMounted, setNestedState]
  )

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {contentType.properties.label || contentType.name}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {contentType.fields.map((contentTypeField) => {
            const data = contentItem.data[contentTypeField.name]
            if (!data || !data.iv) {
              return null
            }

            return (
              <Fieldset
                key={contentTypeField.fieldId}
                legend={contentTypeField.properties.label}
              >
                <ContentItemField
                  classes={classes}
                  value={data.iv}
                  contentTypeField={contentTypeField}
                  handleClickReference={handleClickReference}
                  nestedState={nestedState}
                  integration={integration}
                />
              </Fieldset>
            )
          })}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {nestedState.contentItem && nestedState.contentType && (
        <ContentItemDialog
          isOpen
          onClose={removeNestedState}
          integration={integration}
          contentType={nestedState.contentType}
          contentItem={nestedState.contentItem}
        />
      )}
      <ErrorSnackbar onClose={removeNestedState} open={!!nestedState.error}>
        {nestedState.error ? nestedState.error.message : ''}
      </ErrorSnackbar>
    </>
  )
})

const ContentItemField = React.memo(
  ({
    classes,
    value,
    contentTypeField,
    handleClickReference,
    integration,
    nestedState,
  }) => {
    switch (contentTypeField.properties.fieldType) {
      case 'Json': {
        switch (value.type) {
          case 'heading': {
            return <Typography variant="h5">{value.value}</Typography>
          }
          case 'html': {
            const safeHtml = sanitizeHtml(value.value, {
              // $FlowFixMe
              allowedTags: sanitizeHtml.defaults.allowedTags.concat([
                'u',
                'b',
                'img',
                'span',
              ]),
            })
            return (
              <div
                key={contentTypeField.fieldId}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            )
          }
          case 'image': {
            return (
              <img
                src={value.value}
                alt={contentTypeField.name}
                className={classes.images}
              />
            )
          }
          default: {
            return <Pre>{JSON.stringify(value, null, 2)}</Pre>
          }
        }
      }
      case 'References': {
        const references = value || []
        return (
          <List disablePadding>
            {references.map((referenceId, index) => (
              <ListItem
                key={referenceId}
                divider={index !== references.length - 1}
                disabled={nestedState.isFetching}
                button
                onClick={() => {
                  handleClickReference({
                    referenceId,
                    contentTypeField,
                  })
                }}
              >
                <ListItemText secondary={referenceId}>
                  Entry {index + 1}
                </ListItemText>
              </ListItem>
            ))}
          </List>
        )
      }
      case 'Geolocation': {
        return (
          <Link
            href={`https://www.google.com/maps/place/${value.latitude},${value.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Co-ordinates: {`${value.latitude},${value.longitude}`}
          </Link>
        )
      }
      case 'Assets': {
        return (
          <Grid container spacing={2}>
            {value.map((assetId, index) => (
              <Grid key={index} item xs={12} sm={6} md={4} zeroMinWidth>
                <ContentItemAsset assetId={assetId} integration={integration} />
              </Grid>
            ))}
          </Grid>
        )
      }
      case 'Multi': {
        return (
          <FormGroup>
            {(contentTypeField.properties.allowedValues || []).map(
              (allowedValue) => (
                <FormControlLabel
                  key={allowedValue}
                  control={
                    <Checkbox
                      color="primary"
                      checked={value.includes(allowedValue)}
                      readOnly
                    />
                  }
                  label={allowedValue}
                />
              )
            )}
          </FormGroup>
        )
      }
      case 'DateTime': {
        switch (contentTypeField.properties.editor) {
          case 'Date': {
            return (
              <Typography>{moment(value).format('MMM Do, YYYY')}</Typography>
            )
          }
          case 'DateTime':
          default: {
            return (
              <Typography>
                {moment(value).format('MMM Do, YYYY h:mm a')}
              </Typography>
            )
          }
        }
      }
      case 'Number':
      case 'String':
      default: {
        switch (contentTypeField.properties.editor) {
          case 'Radio': {
            return (
              <FormGroup>
                {(contentTypeField.properties.allowedValues || []).map(
                  (allowedValue) => (
                    <FormControlLabel
                      key={allowedValue}
                      control={
                        <Radio
                          color="primary"
                          checked={value === allowedValue}
                          readOnly
                        />
                      }
                      label={allowedValue}
                    />
                  )
                )}
              </FormGroup>
            )
          }
          case 'TextArea': {
            return <Typography className={classes.textarea}>{value}</Typography>
          }
          case 'Dropdown':
          case 'Input':
          default: {
            return <Typography>{value}</Typography>
          }
        }
      }
    }
  }
)

export default ContentItemDialog
